import {
  ReactNode,
  TransitionEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { Box, Stack } from '@mui/material';
import clsx from 'clsx';

import IconButton from '../../../components/buttons/IconButton';
import * as Icons from '../../../components/icons';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';
import { Typography } from '../../Typography';

import { marketingBannerClassNames } from './classNames';
import { MarketingBannerSize, sizeConfigMap } from './constants';

export type MarketingBannerCloseAnimation = 'collapse' | 'fade';

export type MarketingBannerProps = {
  title: string;
  message: string;
  /** @default 'lg' */
  size?: MarketingBannerSize;
  /** @default true */
  rounded?: boolean;
  /** @default true */
  closable?: boolean;
  /**
   * Close exit style.
   * - `collapse` — height to 0 (solo / last in stack; content moves up)
   * - `fade` — opacity only (more banners remain underneath)
   * @default 'collapse'
   */
  closeAnimation?: MarketingBannerCloseAnimation;
  /** Fired after close animation + unmount. Dismiss is handled internally. */
  onClose?: () => void;
  action?: ReactNode;
  endContent?: ReactNode;
  testId?: string;
  className?: string;
  sx?: SxProps;
};

const gradientBackground = `radial-gradient(
  ellipse 34.56% 169.3% at 19.6% 50%,
  ${tokens.theme.gradient.marketingBannerStop1} 0%,
  ${tokens.theme.gradient.marketingBannerStop2} 50%,
  ${tokens.theme.gradient.marketingBannerStop3} 100%
)`;

export const MarketingBanner = ({
  title,
  message,
  size = 'lg',
  rounded = true,
  closable = true,
  closeAnimation = 'collapse',
  onClose,
  action,
  endContent,
  testId,
  className,
  sx,
}: MarketingBannerProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const sizeConfig = sizeConfigMap[size];
  const collapses = closeAnimation === 'collapse';

  const requestClose = useCallback(() => {
    if (isClosing) {
      return;
    }

    if (!collapses) {
      setIsClosing(true);
      return;
    }

    const el = rootRef.current;
    if (el) {
      el.style.height = `${el.offsetHeight}px`;
      void el.offsetHeight;
    }

    requestAnimationFrame(() => {
      if (el) {
        el.style.height = '0px';
      }
      flushSync(() => setIsClosing(true));
    });
  }, [collapses, isClosing]);

  const handleTransitionEnd = useCallback(
    (event: TransitionEvent<HTMLDivElement>) => {
      if (!isClosing || event.target !== event.currentTarget) {
        return;
      }
      const expectedProperty = collapses ? 'height' : 'opacity';
      if (event.propertyName !== expectedProperty) {
        return;
      }
      setMounted(false);
      onClose?.();
    },
    [collapses, isClosing, onClose]
  );

  if (!mounted) {
    return null;
  }

  return (
    <Box
      ref={rootRef}
      data-testid={testId}
      data-closing={isClosing ? 'true' : undefined}
      data-close-animation={closeAnimation}
      className={clsx(
        marketingBannerClassNames.root,
        marketingBannerClassNames[size],
        className
      )}
      onTransitionEnd={handleTransitionEnd}
      sx={mergeSx(
        {
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          overflow: 'hidden',
          gap: sizeConfig.rootGap,
          paddingBlock: `${sizeConfig.pt}px ${sizeConfig.pb}px`,
          paddingInline: sizeConfig.px,
          marginBlock: 0,
          borderRadius: rounded
            ? tokens.theme.radius[sizeConfig.borderRadiusKey]
            : 0,
          background: gradientBackground,
          color: tokens.theme.fg.structural.primaryOnInverse,
          transition: collapses
            ? 'height 220ms cubic-bezier(0.2, 0, 0, 1), opacity 160ms ease-out, padding-block 220ms cubic-bezier(0.2, 0, 0, 1), margin-block 220ms cubic-bezier(0.2, 0, 0, 1)'
            : 'opacity 160ms ease-out',
          '&[data-closing="true"]': collapses
            ? {
                height: 0,
                opacity: 0,
                paddingBlock: 0,
                marginBlock: 0,
              }
            : {
                opacity: 0,
              },
        },
        sx
      )}
    >
      <Stack
        className={marketingBannerClassNames.content}
        flex={1}
        minWidth={0}
        gap={sizeConfig.contentGap}
      >
        <Stack
          className={marketingBannerClassNames.text}
          gap={sizeConfig.titleGap}
          color="inherit"
        >
          <Typography
            className={marketingBannerClassNames.title}
            variant={sizeConfig.titleVariant}
            color="inherit"
          >
            {title}
          </Typography>
          <Typography
            className={marketingBannerClassNames.message}
            variant="P12R"
            color="inherit"
            sx={{ maxWidth: 600 }}
          >
            {message}
          </Typography>
        </Stack>
        {action && (
          <Box className={marketingBannerClassNames.action}>{action}</Box>
        )}
      </Stack>

      {endContent && (
        <Box
          className={marketingBannerClassNames.endContent}
          sx={{
            alignSelf: 'stretch',
            flexShrink: 0,
            width: sizeConfig.endContentWidth,
            position: 'relative',
          }}
        >
          {endContent}
        </Box>
      )}

      {closable && (
        <IconButton
          className={marketingBannerClassNames.closeButton}
          variant="ghost"
          size="sm"
          onClick={requestClose}
          aria-label="Close"
          testId="marketing-banner-close"
          sx={{
            flexShrink: 0,
            color: tokens.theme.fg.action.onGhostInverse,
            background: 'transparent',
            boxShadow: 'none',
            '&:hover': {
              color: tokens.theme.fg.action.onGhostInverse,
              background: tokens.theme.bg.action.ghostHoverInverse,
              boxShadow: `inset 0 0 0 1px ${tokens.theme.border.action.ghostHoverInverse}`,
            },
            '&:active': {
              color: tokens.theme.fg.action.onGhostInverse,
              background: tokens.theme.bg.action.ghostActiveInverse,
              boxShadow: `inset 0 0 0 1px ${tokens.theme.border.action.ghostActiveInverse}`,
            },
          }}
        >
          <Icons.X weight="bold" />
        </IconButton>
      )}
    </Box>
  );
};
